import {ReactElement, useMemo, useState} from "react";
import * as css from "../styles/revenue-wizard.module.scss";
import MetricSlider from "../components/MetricSlider";
import RoundingControls from "../components/RoundingControls";
import {IUseRevenueWizardState} from "../logic/useRevenueWizard";
import SummaryColumn from "../components/SummaryColumn";
import FormulaInfo from "../components/FormulaInfo";
import {Field, Label, Toggle} from "@zendeskgarden/react-forms";
import * as tableCSS from "../styles/summary-table.module.scss";
import {Button} from "@zendeskgarden/react-buttons";
import {VersionType} from "../../../__generated__/generated_types";
import VZTwoButtonGroup from "../../vz-ui/vz-two-button-group/VZTwoButtonGroup";
import { sanitizeTableInput } from '../../account-table/logic/AccountTableHelpers';


interface IOccupancyAndRenewalsProps {
    revenueWizardInstance: IUseRevenueWizardState;
}

export default function OccupancyAndRenewals({revenueWizardInstance}: IOccupancyAndRenewalsProps): ReactElement {
    const {
        unitTypeData,
        property,
        budgetYear,
        renewalRate,
        occupancyRate,
        setRenewalRate,
        setOccupancyRate,
        convertValuesWithFavor,
        getUnitTypeVersionSummary,
    } = revenueWizardInstance;

    const [showAllUnitTypes, setShowAllUnitTypes] = useState(false);
    const [previewingVersionType, setPreviewingVersionType] = useState<VersionType>(VersionType.Budget);

    const favorRenewalsConvertedData = useMemo(() => {
        if (unitTypeData.length === 0) {
            return {unitTypes: [], summary: null};
        }

        const converted = convertValuesWithFavor(unitTypeData, "renewals");
        return {
            unitTypes: converted,
            summary: getUnitTypeVersionSummary(converted),
        };
    }, [unitTypeData]);

    const favorOccupancyConvertedData = useMemo(() => {
        if (unitTypeData.length === 0) {
            return {unitTypes: [], summary: null};
        }

        const converted = convertValuesWithFavor(unitTypeData, "occupancy");
        return {
            unitTypes: converted,
            summary: getUnitTypeVersionSummary(converted),
        };
    }, [unitTypeData]);

    return (
        <div>
            <div className={css.stepTitleWrapper}>
                <h3 className={css.stepTitle}>Set Occupancy and Renewals</h3>
                <FormulaInfo />
            </div>

            <div className={css.occupancyAndRenewalsWrapper}>
                <div className={css.leftCol}>
                    <p className={css.stepParagraph}>
                        Beginning with any values from your Actuals in the Reforecast period for {property.name}, 
                        we'll use your targets to create two scenarios through the end of your Budget season.
                    </p>

                    <p className={css.stepParagraph}>
                        You can choose the one that gets you closer to your 
                        Renewals goal, or the other which favors hitting your Occupancy goal. <span style={{fontWeight: 500}}>Depending on your data and targets, both scenarios may present the same numbers.</span>
                    </p>

                    <div className={css.metricControlsWrapper}>
                        <RoundingControls
                            revenueWizardInstance={revenueWizardInstance}
                            tooltipPlacement="start"
                        />
                        <MetricSlider
                            rate={renewalRate}
                            title={"Renewal"}
                            onChange={setRenewalRate}
                            isRenewals
                        />
                        <MetricSlider
                            rate={occupancyRate}
                            title={"Occupancy"}
                            onChange={setOccupancyRate}
                        />
                        <div style={{padding: "20px"}}>
                            <Field>
                                <Toggle
                                    checked={showAllUnitTypes}
                                    onChange={(evt) => setShowAllUnitTypes(evt.target.checked)}
                                    aria-label="toggle show all unit types"
                                    >
                                    <Label>Show Detailed Unit Type Breakdown</Label>
                                </Toggle>
                            </Field>
                        </div>
                    </div>
                </div>

                <div className={css.rightCol}>
                    <div style={{textAlign: "center"}}>
                        <VZTwoButtonGroup
                            fieldLabel={"Previewing Season"}
                            firstLabel={`Reforecast ${budgetYear - 1}`}
                            secondLabel={`Budget ${budgetYear}`}
                            onChange={viewingRfcst => setPreviewingVersionType(viewingRfcst ? VersionType.Reforecast : VersionType.Budget)}
                        />
                    </div>
                    <div className={css.summaryColumnGroup}>
                        <SummaryColumn
                            metricFavorPreview={"renewals"}
                            previewingVersionType={previewingVersionType}
                            showAllUnitTypes={showAllUnitTypes}
                            revenueWizardInstance={revenueWizardInstance}
                            convertedUnitTypeData={favorRenewalsConvertedData}
                        />
                        <SummaryColumn
                            metricFavorPreview={"occupancy"}
                            previewingVersionType={previewingVersionType}
                            showAllUnitTypes={showAllUnitTypes}
                            revenueWizardInstance={revenueWizardInstance}
                            convertedUnitTypeData={favorOccupancyConvertedData}
                        />
                    </div>
                </div>

            </div>
        </div>
    );
}
